<template>
  <div class="mobilePage">
    <div class="mobilePage__regionBar">
      <div class="mobilePage__regionTabs">
        <div
          class="mobilePage__regionTabs__slider"
          :class="
            mapRegion === MAP_REGION_CN
              ? 'mobilePage__regionTabs__slider--cn'
              : 'mobilePage__regionTabs__slider--my'
          "
        />
        <button
          type="button"
          class="mobilePage__regionTab"
          :class="{
            'mobilePage__regionTab--active': mapRegion === MAP_REGION_CN,
            'mobilePage__regionTab--disabled': isRegionTabSwitchDisabled(MAP_REGION_CN),
          }"
          @click="switchCountry(MAP_REGION_CN)"
        >
          <span class="mobilePage__regionTab__flag" aria-hidden="true">🇨🇳</span>
          <span class="mobilePage__regionTab__text">{{
            $t("app.home.map_country_china")
          }}</span>
        </button>
        <button
          type="button"
          class="mobilePage__regionTab"
          :class="{
            'mobilePage__regionTab--active': mapRegion === MAP_REGION_MY,
            'mobilePage__regionTab--disabled': isRegionTabSwitchDisabled(MAP_REGION_MY),
          }"
          @click="switchCountry(MAP_REGION_MY)"
        >
          <span class="mobilePage__regionTab__flag" aria-hidden="true">🇲🇾</span>
          <span class="mobilePage__regionTab__text">{{
            $t("app.home.map_country_malaysia")
          }}</span>
        </button>
      </div>
    </div>

    <TianDiTuMobileAllStoresMap
      v-if="useTiandituMap"
      class="mobilePage__map"
      ref="mapRef"
      :dealers="dealerList"
      :mapRegion="mapRegion"
      :activeKey="activeDealerKey"
      :bottomInset="panelBottomInset"
      @map-ready="mapReady = true"
      @marker-click="onMarkerClick"
    />
    <MapTilerMobileAllStoresMap
      v-else
      class="mobilePage__map"
      ref="mapRef"
      :dealers="dealerList"
      :mapRegion="mapRegion"
      :activeKey="activeDealerKey"
      :bottomInset="panelBottomInset"
      :mapLabelLanguage="mapLabelLanguage"
      @map-ready="mapReady = true"
      @marker-click="onMarkerClick"
    />

    <div v-if="!mapReady" class="mobilePage__mapLoading">
      <div class="mobilePage__mapLoading__spinner" />
      <span>{{ $t("app.home.map_loading") }}</span>
    </div>

    <MapTilerMobileAllStoresPanel
      ref="panelRef"
      :groups="groupedDealers"
      :loading="listLoading"
      v-model:keyword="keyword"
      :activeKey="activeDealerKey"
      @select="onSelectDealerFromList"
      @overview="onResetMapOverview"
      @height-change="onPanelHeightChange"
    />

    <transition name="mobilePopup">
      <div
        v-if="showMarkerPopup && selectDealerInfo.fullName"
        class="mobilePopupMask"
        @click.self="showMarkerPopup = false"
      >
        <div class="mobilePopup" @click.stop>
          <van-icon
            name="cross"
            class="mobilePopup__close"
            @click="showMarkerPopup = false"
          />
          <div class="mobilePopup__title">{{ selectDealerInfo.fullName }}</div>
          <div
            v-if="selectDealerInfo.regionText"
            class="mobilePopup__regionText"
          >
            {{ selectDealerInfo.regionText }}
          </div>
          <div v-if="popupDealerList.length > 1" class="mobilePopup__switchList">
            <div
              v-for="(d, idx) in popupDealerList"
              :key="d.__listKey ?? `${d.fullName}-${idx}`"
              class="mobilePopup__switchItem"
              :class="{
                'mobilePopup__switchItem--active': idx === popupDealerIndex,
              }"
              @click="onPickPopupDealer(d, idx)"
            >
              {{ d.fullName || "-" }}
            </div>
          </div>
          <div class="mobilePopup__row" v-if="selectDealerInfo.address">
            <img class="mobilePopup__icon" :src="popupIconLoc" alt="" />
            <div class="mobilePopup__text">{{ selectDealerInfo.address }}</div>
            <button
              type="button"
              class="mobilePopup__copyBtn"
              @click="onCopyAddress"
            >
              {{ $t("app.home.map_popup_copy_address") }}
            </button>
          </div>
          <button type="button" class="mobilePopup__btn" @click="onGotoPlace">
            <img class="mobilePopup__btnIcon" :src="popupIconNav" alt="" />
            <span>{{ $t("app.home.map_popup_nav_to") }}</span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import MapTilerMobileAllStoresMap from "@/views/home/maptiler/MapTilerMobileAllStoresMap.vue";
import TianDiTuMobileAllStoresMap from "@/views/home/maptiler/TianDiTuMobileAllStoresMap.vue";
import MapTilerMobileAllStoresPanel from "@/views/home/maptiler/MapTilerMobileAllStoresPanel.vue";
import {
  groupDealersByCountryProvince,
  getDealerItemKey,
  getDealerDisplayAddress,
  hasValidMapCoordinates,
  isMalaysiaDealer,
  resolveDealerIndexInGroup,
} from "@/views/home/maptiler/useMapTilerAllStores";
import { useMapTilerCopyText } from "@/views/home/maptiler/useMapTilerCopyText";
import { useRouteLangQueryEffect } from "@/views/home/maptiler/useRouteLangQueryEffect";
import {
  getDefaultMapRegion,
  shouldUseAmapForNavigation,
  shouldUseTiandituMap,
} from "@/views/home/maptiler/useMapTilerSite";
import { useMapTilerRegionSwitch } from "@/views/home/maptiler/useMapTilerRegionSwitch";
import { useMapTilerCountryTabSwitch } from "@/views/home/maptiler/useMapTilerCountryTabSwitch";
import { useMapLabelLanguage } from "@/views/home/maptiler/useMapLabelLanguage";
import {
  getChinaProvinceDisplayName,
  localizeGroupedDealersForDisplay,
} from "@/views/home/maptiler/provinceDisplay";
import {
  MAP_REGION_CN,
  MAP_REGION_MY,
  getMapRegionDefaultLocation,
} from "@/utils/config";
import navigatorUtils from "@/utils/navigatorUtilsOptimized";
import popupIconLoc from "@/assets/map-icons/map_location_theme.png";
import popupIconNav from "@/assets/map-icons/navigation.png";

const useNavigatorEffect = (selectDealerInfo, mapRegion, locale) => {
  const onGotoPlace = () => {
    const lang = locale.value === "en-US" ? "en" : "zh";
    const useAmap = shouldUseAmapForNavigation();
    navigatorUtils.navigateToDestinationMobile(
      {
        endLatitude: selectDealerInfo.endLatitude,
        endLongitude: selectDealerInfo.endLongitude,
        name: selectDealerInfo.fullName,
        region: mapRegion.value,
        useAmap,
      },
      { lang }
    );
  };
  return { onGotoPlace };
};

export default {
  name: "HomeMobileMapTiler",
  components: {
    MapTilerMobileAllStoresMap,
    TianDiTuMobileAllStoresMap,
    MapTilerMobileAllStoresPanel,
  },
  setup() {
    const { locale } = useI18n();
    useRouteLangQueryEffect();
    const { mapLabelLanguage } = useMapLabelLanguage();

    const mapRef = ref(null);
    const panelRef = ref(null);
    const mapRegion = ref(getDefaultMapRegion());
    const mapReady = ref(false);
    const listLoading = ref(false);
    const dealerList = ref([]);
    const keyword = ref("");
    const activeDealerKey = ref(null);
    const selectDealerInfo = reactive({});
    const showMarkerPopup = ref(false);
    const popupDealerList = ref([]);
    const popupDealerIndex = ref(0);
    const panelBottomInset = ref(120);
    const useTiandituMap = shouldUseTiandituMap();

    const { copyText } = useMapTilerCopyText();

    const groupedDealers = computed(() =>
      localizeGroupedDealersForDisplay(
        groupDealersByCountryProvince(dealerList.value),
        locale.value,
        mapRegion.value
      )
    );

    const innerDefaultMapCenter = reactive({
      ...getMapRegionDefaultLocation(mapRegion.value),
    });

    const { onGotoPlace } = useNavigatorEffect(
      selectDealerInfo,
      mapRegion,
      locale
    );

    const { switchCountry: switchCountryBase, loadInitialDealers } =
      useMapTilerRegionSwitch({
      mapRegion,
      dealerList,
      listLoading,
      keyword,
      activeDealerKey,
      showMarkerPopup,
      innerDefaultMapCenter,
    });

    const { switchCountry, isRegionTabSwitchDisabled } =
      useMapTilerCountryTabSwitch({
        switchCountryBase: async (region) => {
          panelBottomInset.value = 120;
          await switchCountryBase(region);
        },
        listLoading,
        mapRegion,
      });

    const focusDealerOnMap = (item) => {
      if (!hasValidMapCoordinates(item) || !mapRef.value?.focusDealer) return;
      const lat = item?.latitude ?? item?.lat;
      const lng = item?.longitude ?? item?.lng;
      mapRef.value.focusDealer({
        longitude: Number(lng),
        latitude: Number(lat),
        dealer: item,
      });
    };

    const buildRegionText = (dealer) => {
      if (isMalaysiaDealer(dealer)) {
        return [dealer?.provinceName, dealer?.cityName, dealer?.districtName]
          .filter(Boolean)
          .join(" · ");
      }
      const province = getChinaProvinceDisplayName(
        dealer?.provinceName,
        locale.value
      );
      const parts = [dealer?.countryName, province].filter(Boolean);
      return parts.join(" · ");
    };

    const syncPopupDealerInfo = (dealer) => {
      if (!dealer) return;
      selectDealerInfo.fullName = dealer.fullName;
      selectDealerInfo.address = getDealerDisplayAddress(dealer);
      selectDealerInfo.regionText = buildRegionText(dealer);
      selectDealerInfo.endLatitude =
        dealer.latitude ?? dealer.endLatitude ?? dealer.lat;
      selectDealerInfo.endLongitude =
        dealer.longitude ?? dealer.endLongitude ?? dealer.lng;
    };

    const onCopyAddress = () => {
      copyText(selectDealerInfo.address);
    };

    const collapseBottomPanel = () => {
      panelRef.value?.collapseToMin?.();
    };

    const onSelectDealerFromList = ({ item, key }) => {
      activeDealerKey.value = key;
      if (!item) return;
      collapseBottomPanel();
      focusDealerOnMap(item);
    };

    const onResetMapOverview = () => {
      activeDealerKey.value = null;
      showMarkerPopup.value = false;
      panelBottomInset.value = 120;
      nextTick(() => {
        requestAnimationFrame(() => {
          mapRef.value?.resetOverview?.();
        });
      });
    };

    const onPickPopupDealer = (dealer, idx) => {
      popupDealerIndex.value = idx;
      syncPopupDealerInfo(dealer);
      activeDealerKey.value = dealer?.__listKey ?? null;
    };

    const onPanelHeightChange = (height) => {
      panelBottomInset.value = Math.max(120, Number(height) || 120);
    };

    const onMarkerClick = (location) => {
      if (!location || location.id !== "dealer") {
        showMarkerPopup.value = false;
        return;
      }

      collapseBottomPanel();

      const lat = Number(location.endLatitude ?? location.latitude);
      const lng = Number(location.endLongitude ?? location.longitude);
      const groupedFromMarker = Array.isArray(location.groupDealers)
        ? location.groupDealers
        : [];
      const groupedFromList = (dealerList.value || []).filter((d) => {
        const dLat = Number(d.latitude ?? d.lat);
        const dLng = Number(d.longitude ?? d.lng);
        if (!Number.isFinite(dLat) || !Number.isFinite(dLng)) return false;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        return (
          dLat.toFixed(6) === lat.toFixed(6) && dLng.toFixed(6) === lng.toFixed(6)
        );
      });
      const grouped =
        groupedFromMarker.length > 0 ? groupedFromMarker : groupedFromList;

      popupDealerList.value =
        grouped.length > 0
          ? grouped
          : [
              {
                fullName: location.fullName,
                address: location.address,
                latitude: location.endLatitude ?? location.latitude,
                longitude: location.endLongitude ?? location.longitude,
                __listKey: getDealerItemKey(location, 0),
              },
            ];
      popupDealerIndex.value = resolveDealerIndexInGroup(
        popupDealerList.value,
        activeDealerKey.value
      );
      syncPopupDealerInfo(popupDealerList.value[popupDealerIndex.value]);
      activeDealerKey.value =
        popupDealerList.value[popupDealerIndex.value]?.__listKey ?? null;
      showMarkerPopup.value = true;
    };

    onMounted(() => {
      loadInitialDealers();
    });

    return {
      MAP_REGION_CN,
      MAP_REGION_MY,
      mapRegion,
      mapReady,
      listLoading,
      switchCountry,
      isRegionTabSwitchDisabled,
      dealerList,
      groupedDealers,
      keyword,
      activeDealerKey,
      mapRef,
      panelRef,
      useTiandituMap,
      mapLabelLanguage,
      panelBottomInset,
      onSelectDealerFromList,
      onResetMapOverview,
      onMarkerClick,
      onGotoPlace,
      selectDealerInfo,
      popupIconLoc,
      popupIconNav,
      showMarkerPopup,
      popupDealerList,
      popupDealerIndex,
      onPickPopupDealer,
      onPanelHeightChange,
      onCopyAddress,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/viriables.scss";

.mobilePage {
  position: absolute;
  inset: 0;
  background: #e8e8e8;
  overflow: hidden;

  &__regionBar {
    position: absolute;
    left: 0.12rem;
    top: 0.12rem;
    z-index: 2100;
    padding: 0.04rem;
    border-radius: 0.14rem;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 0.06rem 0.2rem rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(8px);
  }

  &__regionTabs {
    position: relative;
    display: flex;
    width: 2.4rem;
    height: 0.34rem;
    padding: 0.03rem;
    border-radius: 0.1rem;
    background: #f3f5f4;
    box-sizing: border-box;
  }

  &__regionTabs__slider {
    position: absolute;
    top: 0.03rem;
    bottom: 0.03rem;
    width: calc(50% - 0.03rem);
    border-radius: 0.08rem;
    background: linear-gradient(135deg, #00a34a 0%, $themeColor 100%);
    box-shadow: 0 0.02rem 0.08rem rgba(0, 147, 67, 0.35);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
  }

  &__regionTabs__slider--cn {
    transform: translateX(0);
  }

  &__regionTabs__slider--my {
    transform: translateX(calc(100% + 0.03rem));
  }

  &__regionTab {
    position: relative;
    z-index: 1;
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.04rem;
    border: none;
    background: transparent;
    color: #666;
    font-size: 0.12rem;
    line-height: 1;
    padding: 0;
    transition: color 0.28s cubic-bezier(0.4, 0, 0.2, 1);

    &__flag {
      font-size: 0.14rem;
      line-height: 1;
    }

    &__text {
      font-weight: 500;
    }

    &--active {
      color: #fff;
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  &__map {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  &__mapLoading {
    position: absolute;
    inset: 0;
    z-index: 1500;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.12rem;
    background: rgba(245, 247, 246, 0.72);
    color: #666;
    font-size: 0.12rem;
    pointer-events: none;

    &__spinner {
      width: 0.28rem;
      height: 0.28rem;
      border: 0.03rem solid rgba(0, 147, 67, 0.15);
      border-top-color: $themeColor;
      border-radius: 50%;
      animation: mobileMapSpin 0.8s linear infinite;
    }
  }
}

@keyframes mobileMapSpin {
  to {
    transform: rotate(360deg);
  }
}

.mobilePopupMask {
  position: fixed;
  inset: 0;
  z-index: 1999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  padding: 0.16rem;
  box-sizing: border-box;
}

.mobilePopup {
  position: relative;
  width: 3.2rem;
  max-width: 100%;
  background: #fff;
  border-radius: 0.12rem;
  box-shadow: 0 0.06rem 0.24rem rgba(0, 0, 0, 0.18);
  padding: 0.18rem 0.16rem 0.16rem;
  box-sizing: border-box;

  &__close {
    position: absolute;
    right: 0.1rem;
    top: 0.1rem;
    font-size: 0.18rem;
    color: #999;
  }

  &__title {
    font-size: 0.14rem;
    font-weight: 700;
    color: #383838;
    line-height: 1.25;
    padding-right: 0.28rem;
    text-align: left;
  }

  &__regionText {
    margin-top: 0.06rem;
    font-size: 0.11rem;
    color: $themeColor;
    text-align: left;
  }

  &__switchList {
    margin-top: 0.08rem;
    max-height: 1.2rem;
    overflow-y: auto;
    border: 1px solid #e8e8e8;
    border-radius: 0.08rem;
  }

  &__switchItem {
    padding: 0.08rem 0.1rem;
    font-size: 0.12rem;
    color: #383838;
    text-align: left;
    border-bottom: 1px solid #f2f2f2;
  }

  &__switchItem:last-child {
    border-bottom: none;
  }

  &__switchItem--active {
    color: #009343;
    background: rgba(0, 147, 67, 0.08);
    font-weight: 600;
  }

  &__row {
    display: flex;
    gap: 0.08rem;
    margin-top: 0.12rem;
    align-items: flex-start;
  }

  &__icon {
    width: 0.16rem;
    height: 0.16rem;
    margin-top: 0.02rem;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__text {
    flex: 1;
    font-size: 0.12rem;
    color: #383838;
    line-height: 1.25;
    word-break: break-all;
    text-align: left;
  }

  &__copyBtn {
    flex-shrink: 0;
    border: none;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.1rem;
    line-height: 1;
    padding: 0.05rem 0.08rem;
    border-radius: 0.04rem;
  }

  &__btn {
    width: 100%;
    margin-top: 0.16rem;
    height: 0.42rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.06rem;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #00a34a 0%, $themeColor 100%);
    color: #fff;
    font-size: 0.14rem;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0.04rem 0.14rem rgba(0, 147, 67, 0.38);
    transition: opacity 0.15s ease, transform 0.15s ease;

    &:active {
      transform: scale(0.98);
      opacity: 0.92;
    }
  }

  &__btnIcon {
    width: 0.14rem;
    height: 0.14rem;
    flex-shrink: 0;
    filter: brightness(0) invert(1);
  }
}

.mobilePopup-enter-active,
.mobilePopup-leave-active {
  transition: opacity 0.25s ease-out;
}

.mobilePopup-enter-from,
.mobilePopup-leave-to {
  opacity: 0;
}

.mobilePopup-enter-active .mobilePopup,
.mobilePopup-leave-active .mobilePopup {
  transition: transform 0.25s ease-out, opacity 0.25s ease-out;
}

.mobilePopup-enter-from .mobilePopup {
  transform: translateY(24px) scale(0.9);
  opacity: 0;
}

.mobilePopup-leave-to .mobilePopup {
  transform: translateY(24px) scale(0.9);
  opacity: 0;
}
</style>
